/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { memoize } from 'lodash';
import type { FeatureLike } from 'ol/Feature';
import { Point } from 'ol/geom';
import { Circle, Fill, Stroke, Style, Text } from 'ol/style';
import { arrowUpPath } from '@opengeoweb/theme';
import type { ColorMap } from './colormaps';
import { colorMaps } from './colormaps';
import { createIconStyle } from '../../components/OpenLayers/styles/useIconStyle';

export interface EdrMapLegendEntry {
  min: number;
  max: number;
  color: string;
  image: Circle;
}

export interface EdrOlColorLegend {
  legend: EdrMapLegendEntry[];
}

export const makeLegendFromStyleName = (
  styleName: string,
): EdrOlColorLegend | null => {
  const minMaxPartFromNumericIndex = styleName.indexOf(':');
  const legMinMax =
    minMaxPartFromNumericIndex > 0
      ? styleName
          .substring(minMaxPartFromNumericIndex + 1)
          .split(',')
          .map((stringValue) => parseFloat(stringValue))
      : [undefined, undefined];
  const min = legMinMax.length === 2 ? legMinMax[0] : undefined;
  const max = legMinMax.length === 2 ? legMinMax[1] : undefined;

  const legendName = styleName?.split(':')[0];
  if (legendName === 'temperatureLegendColorsWoW') {
    const legend = makeLegendFromColorMap({
      colors: colorMaps.temperatureLegendColorsWoW,
      min: min !== undefined ? min : -18,
      max: max !== undefined ? max : 38,
    });
    return legend;
  }
  if (legendName === 'temperatureLegendColorsCWK') {
    return makeLegendFromColorMap({
      colors: colorMaps.temperatureLegendColorsCWK,
      min: min !== undefined ? min : -18,
      max: max !== undefined ? max : 38,
    });
  }
  if (legendName === 'precipitationMMLegendColorsWoW') {
    return makeLegendFromColorMap({
      colors: colorMaps.precipitationMMLegendColorsWoW,
      min: min !== undefined ? min : 0,
      max: max !== undefined ? max : 30,
    });
  }
  if (legendName === 'wind/direction-speed') {
    return { legend: windLegend };
  }
  return null;
};

export const makeLegendFromColorMap = (props: {
  colors: ColorMap;
  min: number;
  max: number;
}): EdrOlColorLegend => {
  const numClasses = props.colors.length;
  const minVal = props.min;
  const maxVal = props.max;

  const legend: EdrMapLegendEntry[] = props.colors.map(
    (color, index): EdrMapLegendEntry => {
      const distance = (maxVal - minVal) / numClasses;
      const min = index * distance + minVal;
      const max = (index + 1) * distance + minVal;
      return {
        min,
        max,
        color,
        image: new Circle({
          radius: 18,
          fill: new Fill({
            color,
          }),
          stroke: new Stroke({
            color: '#000000',
            width: 1.2,
          }),
        }),
      };
    },
  );
  return { legend };
};

const formatNumber = (value: number): string => {
  return Number.isInteger(value)
    ? value.toString()
    : parseFloat(value.toFixed(2)).toString();
};

export const generateImageFromLegend = (
  edrColorLegend: EdrOlColorLegend,
  ctx: CanvasRenderingContext2D,
  opacity?: number,
): number => {
  const x = 10;
  const yStart = 10;
  const boxSize = 16; // square size
  const gap = 10; // gap between box and text
  const rowSpacing = 2; // vertical spacing between rows
  const font = '11px Arial';

  // Compute required canvas height
  const legendCount = edrColorLegend.legend.length;
  const requiredHeight = yStart + legendCount * (rowSpacing + boxSize);
  const { canvas } = ctx;
  canvas.height = requiredHeight;

  ctx.save();
  // eslint-disable-next-line no-param-reassign
  ctx.font = font;
  // eslint-disable-next-line no-param-reassign
  ctx.textBaseline = 'middle';
  // eslint-disable-next-line no-param-reassign
  ctx.fillStyle = '#111';

  // sort the legend based on highest value first
  const sortedLegend = [...edrColorLegend.legend].sort(
    (itemA, itemB) => itemB.max - itemA.max,
  );

  sortedLegend.forEach((item, index) => {
    const { min, max, color } = item;
    const text = `${formatNumber(min)} : ${formatNumber(max)}`;
    const y = yStart + index * (rowSpacing + boxSize);

    // Draw colored box
    // eslint-disable-next-line no-param-reassign
    ctx.globalAlpha = opacity || 1;
    // eslint-disable-next-line no-param-reassign
    ctx.fillStyle = color;
    ctx.fillRect(x, y, boxSize, boxSize);

    // Draw text
    // eslint-disable-next-line no-param-reassign
    ctx.globalAlpha = 1;
    // eslint-disable-next-line no-param-reassign
    ctx.fillStyle = '#111';
    ctx.fillText(text, x + boxSize + gap, y + boxSize / 2);
  });

  ctx.restore();

  return requiredHeight;
};

export const getLegendClass = (
  edrColorLegend: EdrOlColorLegend,
  value: number,
  clip = false,
): EdrMapLegendEntry | undefined => {
  if (clip) {
    const distances = edrColorLegend.legend.map((entry) => {
      return {
        distance: Math.abs((entry.min + entry.max) / 2 - value),
        entry,
      };
    });
    distances.sort((d1, d2) => d1.distance - d2.distance);
    return distances[0].entry;
  }
  return edrColorLegend.legend.find(
    (entry) => value >= entry.min && value < entry.max,
  );
};

const getOffsetValues = (factor: number): number[][] => {
  return [
    [0, -20],
    [0, 0],
    [0, 20],
    [-30 * factor, -20],
    [-30 * factor, 0],
    [-30 * factor, 20],
    [30 * factor, -20],
    [30 * factor, 0],
    [30 * factor, 20],
    [60 * factor, -20],
    [60 * factor, 0],
    [60 * factor, 20],
    [-60 * factor, -20],
    [-60 * factor, 0],
    [-60 * factor, 20],
  ];
};

const windLegend: EdrMapLegendEntry[] = [
  { color: '#FF0000', min: 48, max: 200, image: new Circle() },
  { color: '#FEAE02', min: 34, max: 47, image: new Circle() },
  { color: '#FBFE02', min: 8, max: 33, image: new Circle() },
  // 8 should be 22 but using 8 to see colors in the demo story with live data when not much wind
  { color: '', min: 0, max: 7, image: new Circle() },
];

const getWindColor = (speed: number): string =>
  windLegend.find((item) => speed >= item.min && speed <= item.max)?.color ||
  '';

export const makeFeatureStyleWind = (feature: FeatureLike): Style[] => {
  const { values } = feature.getProperties();
  if (!values || Object.values(values).length !== 2) {
    return [];
  }

  const directionValue = Number(Object.values(values)[0]);
  const speedValue = Math.round(Number(Object.values(values)[1]));
  const angle = ((directionValue - 180) * Math.PI) / 180;
  const geometry = feature.getGeometry();
  if (!geometry || !(geometry instanceof Point)) {
    return [];
  }

  const arrowStyle = createIconStyle(arrowUpPath, {
    icon: {
      scale: 1.2,
      rotation: angle, // rotate to match wind direction
      anchor: [0, 0],
      rotateWithView: true,
    },
    render: {
      fillStyle: '#000',
      strokeStyle: getWindColor(speedValue),
      lineWidth: 1,
    },
  });

  // speed label
  const speedStyle = new Style({
    text: new Text({
      text: speedValue.toString(),
      offsetY: -10,
      stroke: new Stroke({ color: '#fff', width: 1 }),
      fill: new Fill({ color: '#000' }),
      font: 'bold 11px Arial',
      padding: [-5, -5, -5, -5],
    }),
    geometry,
  });

  return [speedStyle, arrowStyle];
};

export const makeFeatureStyleMultiParam = (feature: FeatureLike): Style[] => {
  const { values } = feature.getProperties();
  const paramValues: number[] = Object.values(values);

  // Determine offset between columns (long strings need more space)
  const maxLength = Math.max(
    ...paramValues.map((el) => (el ? el.toString().length : 1)),
  );
  const factor = maxLength > 5 ? 1.4 : 1;
  const offsetValues = getOffsetValues(factor);

  // Don't allow showing more than 15 params
  const params = paramValues.slice(0, 15);

  return params.map((paramVal, index) => {
    return new Style({
      text: new Text({
        text: paramVal ? paramVal.toString() : '-',
        offsetX: offsetValues[index][0],
        offsetY: offsetValues[index][1],
        scale: 1.0,
        stroke: new Stroke({ color: '#ffffffff', width: 1 }),
        fill: new Fill({ color: '#000000' }),
        font: 'bold 11px Arial',
        padding: [-5, -5, -5, -5],
      }),
    });
  });
};

export const makeFeatureStyleMultiParamLoading = (): Style => {
  return new Style({
    text: new Text({
      scale: 1.2,
      stroke: new Stroke({ color: '#ffffffff', width: 3 }),
      fill: new Fill({ color: '#000000' }),
      text: '...',
    }),
  });
};

export const makeFeatureStyleDisc = (
  feature: FeatureLike,
  legend: EdrOlColorLegend,
): Style => {
  const { values } = feature.getProperties();

  const textValue = Object.values(values)[0];
  const legendEntry = getLegendClass(legend, textValue as number);

  return new Style({
    image: legendEntry?.image!,
    text: new Text({
      font: 'Arial',
      scale: 1.4,
      fill: new Fill({ color: '#000000' }),
      text: `${textValue}`,
    }),
  });
};

export const makeFeatureStyleLoading = (feature: FeatureLike): Style => {
  const { values } = feature.getProperties();

  const textValue = Object.values(values)[0];
  return new Style({
    image: new Circle({
      radius: 14,
      fill: new Fill({
        color: '#808080',
      }),
      stroke: new Stroke({
        color: '#404040',
        width: 1.2,
      }),
    }),
    text: new Text({
      font: 'Arial',
      scale: 1.2,
      fill: new Fill({ color: '#303030' }),
      text: textValue as string,
    }),
  });
};

const defaultLegend = makeLegendFromColorMap({
  colors: colorMaps.temperatureLegendColorsWoW,
  min: -18,
  max: 38,
});

/**
 * Converts a stylename to openlayers style for features
 * @param styleName Strings like temperatureLegendColorsWoW:0,20 will get the legend temperatureLegendColorsWoW with a range from 0,20. This makes it flexible to adjust the colorrange via the stylename.
 * @returns StyleLike
 */
const styleNameToStyleLikeNotMemoed = (
  styleName: string,
  multiParam?: boolean,
): ((feature: FeatureLike) => Style | Style[]) => {
  if (styleName === 'wind/direction-speed') {
    return (feature: FeatureLike): Style[] => makeFeatureStyleWind(feature);
  }

  if (multiParam) {
    return (feature: FeatureLike): Style[] =>
      makeFeatureStyleMultiParam(feature);
  }

  const legend = makeLegendFromStyleName(styleName) || defaultLegend;
  return (feature: FeatureLike): Style => makeFeatureStyleDisc(feature, legend);
};

export const styleNameToStyleLike = memoize(styleNameToStyleLikeNotMemoed);

export const defaultEdrStyles = [
  {
    name: 'temperatureLegendColorsWoW:-18,38',
    title: 'WOW Legend -18 till 18 degrees',
    abstract: 'temperatureLegendColorsWoW',
    legendURL: '',
  },
  {
    name: 'temperatureLegendColorsWoW:0,20',
    title: 'WOW Legend 0 till 20 degrees',
    abstract: 'temperatureLegendColorsWoW',
    legendURL: '',
  },
  {
    name: 'temperatureLegendColorsWoW:10,20',
    title: 'WOW Legend 10 till 20 degrees',
    abstract: 'temperatureLegendColorsWoW',
    legendURL: '',
  },
  {
    name: 'temperatureLegendColorsCWK',
    title: 'temperatureLegendColorsCWK',
    abstract: 'temperatureLegendColorsCWK',
    legendURL: '',
  },
  {
    name: 'precipitationMMLegendColorsWoW:0,30',
    title: '0 till 30 mm/h',
    abstract: 'precipitationMMLegendColorsWoW',
    legendURL: '',
  },
  {
    name: 'precipitationMMLegendColorsWoW:0,3',
    title: '0 till 3 mm/h',
    abstract: 'precipitationMMLegendColorsWoW',
    legendURL: '',
  },
  {
    name: 'wind/direction-speed',
    title: 'Wind direction and speed',
    abstract: '',
    legendURL: '',
  },
];
